import { ArrowLeftCircleIcon, ArrowRightCircleIcon, MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/solid";
import { Button } from "@moe/oss/ui/button";
import { Textarea } from "@moe/oss/ui/textarea";
import { TokenCounter } from "@web/components/TokenCounter";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

interface Props {
  value?: string[];
  onChange: (value: string[]) => void;
}

export const AlternativeGreetingsField = ({ value = [""], onChange }: Props) => {
  const greetings = value?.length ? value : [""];
  const [currentIndex, setCurrentIndex] = useState(0);
  const { createDialog } = useAppContext();

  /**
   * Adds a greeting, navigating to the end of the list
   */
  const handleAddGreeting = () => {
    onChange([...greetings, ""]);
    setCurrentIndex(greetings.length);
  };

  /**
   * Removes a greeting at a certain index, navigating to the previous greeting.
   */
  const removeGreeting = (index: number) => {
    const newGreetings = greetings.filter((_, i) => i !== index);
    onChange(newGreetings);
    setCurrentIndex(Math.min(currentIndex, newGreetings.length - 1));
  };

  /**
   * Handle a greetign removal event,
   * showing a confimation dialog first before removing the greeting if it's not empty.
   */
  const handleRemoveGreeting = (index: number) => {
    if (greetings.length === 1) return;
    if (greetings[index].trim() === "") {
      removeGreeting(index);
      return;
    }
    createDialog({
      title: "Confirm Deletion",
      description: "Are you sure you want to delete this greeting?",
      actionLabel: "Delete",
      onAction: () => removeGreeting(index)
    });
  };

  /**
   * Update greeting value at an index on change
   */
  const handleGreetingChange = (index: number, newValue: string) => {
    const newGreetings = [...greetings];
    newGreetings[index] = newValue;
    onChange(newGreetings);
  };

  const navigateNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, greetings.length - 1));
  };

  const navigatePrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  return (
    <div className="mb-8 w-full">
      <div className="flex items-center justify-between sm:mb-0">
        <div className="flex items-center gap-2">
          <span className="text-tx-secondary mb-1 hidden text-sm font-semibold sm:block md:text-lg">
            Alternative Greetings
          </span>
          <span className="text-tx-secondary mb-1 text-sm font-semibold sm:hidden md:text-lg">Alt. Greetings</span>
          {greetings.length > 1 && (
            <div className="pointer-events-none">
              <div className="flex items-center justify-between px-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={navigatePrev}
                  className="text-tx-secondary hover:text-tx-primary bg-card/90 pointer-events-auto flex items-center justify-center rounded-full transition duration-200"
                >
                  <ArrowLeftCircleIcon className="size-8" />
                </Button>
                <span className="text-tx-secondary flex items-center font-mono text-sm md:text-base">
                  {currentIndex + 1}/{greetings.length}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={navigateNext}
                  className="text-tx-secondary hover:text-tx-primary bg-card/90 pointer-events-auto flex items-center justify-center rounded-full transition duration-200"
                >
                  <ArrowRightCircleIcon className="size-8" />
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            {greetings.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveGreeting(currentIndex)}
                className="text-tx-secondary hover:text-tx-primary transition duration-200"
              >
                <MinusCircleIcon className="h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleAddGreeting}
              className="text-tx-secondary hover:text-tx-primary transition duration-200"
            >
              <PlusCircleIcon className="h-4 w-4" />
            </Button>
          </div>
          <TokenCounter content={greetings[currentIndex] ?? ""} />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.12 }}
          className="relative w-full"
        >
          <Textarea
            value={greetings[currentIndex]}
            onChange={(e) => handleGreetingChange(currentIndex, e.target.value)}
            placeholder="You may enter additional greetings that users of your character can swap between..."
            className="min-h-[120px] w-full text-base font-medium"
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
